/**
 * ============================================================================
 * Martial Arts — the image slot. EMPTY BY DESIGN, and it must stay valid empty.
 * ============================================================================
 *
 * The page ships with **zero images** and is complete without them: every gate,
 * including the pre-render's visible-text floor and the AA sweep, passes on an
 * empty map. This file exists so that adding one later is a data edit against a
 * typed seam rather than a change to the component that renders it.
 *
 * WHY A MAP AND NOT A PATH IN THE JSON. `src/content/data/*.json` is inert
 * data with no module graph. A string like `"./scans/ncc-b.jpg"` written there
 * would never reach Vite's asset pipeline: it would ship unhashed and
 * unoptimised, or more likely 404, and it would sit outside the build's own
 * accounting — which is the class of thing Article V.7 exists to keep out of
 * this repository. An imported binding is content-hashed into `build/assets/`
 * exactly like every other image on the site, and the leak sweep walks it.
 *
 * ----------------------------------------------------------------------------
 * TO ADD AN IMAGE (all four steps, in order)
 * ----------------------------------------------------------------------------
 *   1. REDACT THE SCAN FIRST. Not last, not "before deploy" — before the file
 *      is copied into the repository at all, because git keeps what it is
 *      given and D-028 is the record of how expensive removing it later is.
 *      Mask, in this order:
 *        - the regimental number (deliberately absent from the whole content
 *          layer — an unredacted scan would re-introduce it by the back door)
 *        - the date of birth
 *        - every signature
 *        - the certificate serial number
 *      Names may remain. If a field is unreadable after masking, that is the
 *      correct outcome; the achievement is evidenced by the certificate's
 *      title and grade, not by its serial.
 *
 *   2. Put the prepared file in `src/assets/martial-arts/`.
 *
 *   3. Export it from `src/assets/index.ts` beside the certificate scans.
 *
 *   4. Add one entry below, keyed by the record's `id` from
 *      `martialArts.json`. `alt` is REQUIRED by the type — an undescribed scan
 *      is decoration, not evidence, and a screen-reader user gets nothing from
 *      it.
 *
 * Example, once a redacted scan exists:
 *
 *   import { NCC_Certificate_B_Redacted } from "../assets";
 *
 *   export const MARTIAL_ARTS_IMAGES: Record<string, MartialArtsImage> = {
 *       "ncc-certificate-b": {
 *           src: NCC_Certificate_B_Redacted,
 *           alt: "NCC Certificate B, Grade A, 2017 — redacted scan",
 *           imageType: "certificate"
 *       }
 *   };
 *
 * Candidates that could take a scan later are listed in docs/TODO.md rather
 * than here, so this file stays a mechanism and not a wish list.
 */

import type { MartialArtsImage } from "../types/content";

/**
 * Record id -> image. Empty is the shipped state.
 *
 * Keys must match an `id` in `martialArts.json` — an achievement, grade, role,
 * camp or certificate id. A key matching nothing renders nothing rather than
 * throwing, but it also fails the join assertion in the verification sweep,
 * which is where a typo surfaces.
 */
export const MARTIAL_ARTS_IMAGES: Record<string, MartialArtsImage> = {};

/** The image for a record id, or undefined — the normal case today. */
export const imageFor = (id: string): MartialArtsImage | undefined => MARTIAL_ARTS_IMAGES[id];
